import * as React from 'react'
import type { EventItem } from '~/client/components/event/EventsDropdown'
import { EventCredenza, type EventCredenzaStep } from './EventCredenza'
import { AddToEventStep } from './steps/AddToEventStep'
import type { CreatedEventPayload } from './steps/CreateEventStep'
import { CreateEventModalForm } from './steps/CreateEventModalForm'
import { APIProvider } from '@vis.gl/react-google-maps'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '~/client/components/ui/tabs'
import { SelectShareableListStep } from '~/client/components/shareable-list/SelectShareableListStep'
import { CreateShareableListStep } from '~/client/components/shareable-list/CreateShareableListStep'
import { hasFeature, FeatureFlag } from '~/client/common/features'
import { useEffectiveUserDetail } from '~/client/common/stores/auth'

type Props = {
  open?: boolean
  startStep?: EventCredenzaStep
  onOpenChange?: (open: boolean) => void
  onCreateEvent?: (payload: CreatedEventPayload) => void
  onSelectEvent?: (ev: EventItem) => void
  shouldAddSpaceOnCreate?: boolean
  spaceId?: number
}

const STEP_TITLES: Partial<Record<EventCredenzaStep, string>> = {
  'create-event-form': 'Create Event',
  'create-shareable-list': 'Create New Collection',
  'create-event': 'Create an event',
}

function getConfirmationTitle(confirmState: { eventCreated: boolean; spaceAdded: boolean }): string {
  if (confirmState.eventCreated && confirmState.spaceAdded) return 'Event created and space added'
  if (confirmState.eventCreated) return 'Event created'
  if (confirmState.spaceAdded) return 'Added to event'
  return 'Success'
}

export const AddEventToSpaceDialog = ({
  open = true,
  startStep = 'add-to-event',
  onOpenChange,
  onCreateEvent,
  onSelectEvent,
  shouldAddSpaceOnCreate = true,
  spaceId,
}: Props) => {
  const [step, setStep] = React.useState<EventCredenzaStep>(startStep)
  const [confirmState, setConfirmState] = React.useState<{ eventCreated: boolean; spaceAdded: boolean }>({
    eventCreated: false,
    spaceAdded: false,
  })
  const [activeTab, setActiveTab] = React.useState<string>('events')
  const effectiveUser = useEffectiveUserDetail()
  const hasCollectionsFeature = hasFeature(effectiveUser, FeatureFlag.COLLECTIONS)

  React.useEffect(() => {
    if (open) {
      setStep(startStep)
      setActiveTab('events')
    }
  }, [open])

  let title: string
  if (step === 'add-to-event') {
    title = activeTab === 'collections' ? 'Add to Collection' : 'Add to Event'
  } else if (step === 'added-to-event') {
    title = getConfirmationTitle(confirmState)
  } else {
    title = STEP_TITLES[step] ?? 'Success'
  }

  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY} libraries={['places']}>
      <EventCredenza open={open} onOpenChange={onOpenChange} step={step} title={title} className={`bg-white${step === 'create-event-form' ? ' pb-4' : ''}`}>
        {step === 'create-event-form' ? (
          <CreateEventModalForm
            onCancel={() => onOpenChange?.(false)}
            onCreate={(payload) => {
              setConfirmState({ eventCreated: true, spaceAdded: !!shouldAddSpaceOnCreate })
              onCreateEvent?.(payload)
            }}
            spaceId={spaceId}
            shouldAddSpaceOnCreate={shouldAddSpaceOnCreate}
          />
        ) : null}
        {step === 'add-to-event' ? (
          <Tabs defaultValue="events" onValueChange={setActiveTab}>
            {hasCollectionsFeature && (
              <TabsList className="w-full">
                <TabsTrigger value="events" className="flex-1">Events</TabsTrigger>
                <TabsTrigger value="collections" className="flex-1">Collections</TabsTrigger>
              </TabsList>
            )}
            <TabsContent value="events">
              <AddToEventStep
                spaceId={spaceId}
                onSelectEvent={(ev) => {
                  onSelectEvent?.(ev)
                  setConfirmState({ eventCreated: false, spaceAdded: true })
                  onOpenChange?.(false)
                }}
                onCreateEvent={() => setStep('create-event-form')}
              />
            </TabsContent>
            {hasCollectionsFeature && (
              <TabsContent value="collections">
                <SelectShareableListStep
                  spaceId={spaceId}
                  onCreateNew={() => setStep('create-shareable-list')}
                  onDone={() => onOpenChange?.(false)}
                />
              </TabsContent>
            )}
          </Tabs>
        ) : null}
        {step === 'create-event' ? (
          <CreateEventModalForm
            onCancel={() => onOpenChange?.(false)}
            onCreate={(payload) => {
              setConfirmState({ eventCreated: true, spaceAdded: !!shouldAddSpaceOnCreate })
              onCreateEvent?.(payload)
            }}
            spaceId={spaceId}
            shouldAddSpaceOnCreate={shouldAddSpaceOnCreate}
          />
        ) : null}
        {step === 'create-shareable-list' ? (
          <CreateShareableListStep
            spaceId={spaceId}
            onBack={() => setStep('add-to-event')}
            onDone={() => onOpenChange?.(false)}
          />
        ) : null}
      </EventCredenza>
    </APIProvider>
  )
}
